import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {useHistory, useParams} from "react-router-dom"
import {useSelector} from "react-redux"
import {BridgeDeviceModel} from "../../../../types/bridge-device"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useMessage} from "../../../../hooks/message"
import {useQuestion} from "../../../../hooks/question"
import {useRPC} from "../../../../hooks/rpc"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useTranslation} from "react-i18next"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import {BRIDGE_DEVICE_TYPE_ID_EIO, BRIDGE_DEVICE_TYPE_ID_ICM, BRIDGE_DEVICE_TYPE_ID_INO_PAN_3, BRIDGE_DEVICE_TYPE_ID_KNX, BRIDGE_DEVICE_TYPE_ID_STP, BRIDGE_DEVICE_TYPE_ID_STS_PLUS} from "../../../../types/control"

const BACK_URL = "/settings/device"

export const DeviceSettingsScreen = memo(function DeviceSettingsScreen() {
    const {t} = useTranslation()
    const params = useParams<{id: string}>()
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard("numpad", true)
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] = useKeyboard(
        "numpad-extended",
        true
    )
    const {success, failure} = useMessage()
    const question = useQuestion()
    const [rpc] = useRPC()
    const history = useHistory()

    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList)
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList)

    const [selected, setSelected] = useState<BridgeDeviceModel>(null)
    const [isTemperatureDevice, setTemperatureDevice] = useState(false)
    const [temperatureOffset, setTemperatureOffset] = useState("0")
    const [temperatureHysteresis, setTemperatureHysteresis] = useState("0.2")
    const [firstLevelAlarmCountdownDuration, setFirstLevelAlarmCountdownDuration] = useState("0")
    const [secondLevelAlarmCountdownDuration, setSecondLevelAlarmCountdownDuration] = useState("0")
    const [thirdLevelAlarmCountdownDuration, setThirdLevelAlarmCountdownDuration] = useState("0")
    const [reactivationDurationAfterReset, setReactivationDurationAfterReset] = useState("0")
    const [sirenIntervalOnDuration, setSirenIntervalOnDuration] = useState("0")
    const [sirenIntervalOffDuration, setSirenIntervalOffDuration] = useState("0")
    const [sirenRepeatCount, setSirenRepeatCount] = useState("0")
    const [matchInProgress, setMatchInProgress] = useState(false)
    const isGlassDevice = bridgeDeviceTypeSet[selected?.type_id]?.is_io_device
    const [frequency, setFrequency] = useState<SelectItem<number> | null>(null);
    const [duty, setDuty] = useState<SelectItem<number> | null>(null);
    const isICMDevice = selected?.type_id === BRIDGE_DEVICE_TYPE_ID_ICM;


    // Frequency and duty options is used by icm devices. IDK whatever it is.
    const FREQUENCY_OPTIONS = useMemo<SelectItem<number>[]>(() => {
        const options: SelectItem<number>[] = [];
        for (let i = 11; i <= 65; i++) {
            options.push({
                label: `${i} kHz`,
                value: i
            });
        }
        return options;
    }, [])

    const DUTY_OPTIONS = useMemo<SelectItem<number>[]>(() => {
        const options: SelectItem<number>[] = [];
        for (let i = 10; i <= 90; i++) {
            options.push({
                label: `${i}%`,
                value: i
            });
        }
        return options;
    }, [])

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL)
    }, [history])

    const SLEEP_MODE_OPTIONS = useMemo<SelectItem<number>[]>(
        () => [
            {
                label: t("off"),
                value: 0
            },
            {
                label: `${t("on") + "(30s)"}`,
                value: 30
            },
            {
                label: `${t("on") + "(60s)"}`,
                value: 60
            },
            {
                label: `${t("on") + "(120s)"}`,
                value: 120
            },
            {
                label: `${t("on") + "(180s)"}`,
                value: 180
            }
        ],
        [t]
    )

    const [sleepMode, setSleepMode] = useState<SelectItem<number>>(
        selected == null
            ? SLEEP_MODE_OPTIONS[0]
            : SLEEP_MODE_OPTIONS.find(sleepOption => sleepOption.value === selected?.lucent_config?.sleep_time) || null
    )

    const SENSITIVITY_MODE_OPTIONS = useMemo<SelectItem<string>[]>(
        () => [
            {
                label: t("default"),
                value: "0"
            },
            {
                label: t("low"),
                value: "1"
            },
            {
                label: t("high"),
                value: "2"
            }
        ],
        [t]
    )
    const [sensitivityMode, setSensitivityMode] = useState<SelectItem<string>>(
        selected == null
            ? SENSITIVITY_MODE_OPTIONS[0]
            : SENSITIVITY_MODE_OPTIONS.find(
                  sleepOption => sleepOption.value === selected?.lucent_config?.sensitivity_mode
              ) || null
    )

    const SENSITIVITY_LEFT_MODE_OPTIONS = useMemo<SelectItem<string>[]>(
        () => [
            {
                label: t("default"),
                value: "0"
            },
            {
                label: t("low"),
                value: "1"
            },
            {
                label: t("high"),
                value: "2"
            }
        ],
        [t]
    )
    const [sensitivityLeftMode, setSensitivityLeftMode] = useState<SelectItem<string>>(
        selected == null
            ? SENSITIVITY_LEFT_MODE_OPTIONS[0]
            : SENSITIVITY_LEFT_MODE_OPTIONS.find(
                  sleepOption => sleepOption.value === selected?.lucent_config?.sensitivity_left_touch
              ) || null
    )

    const SENSITIVITY_RIGHT_MODE_OPTIONS = useMemo<SelectItem<string>[]>(
        () => [
            {
                label: t("default"),
                value: "0"
            },
            {
                label: t("low"),
                value: "11"
            },
            {
                label: t("high"),
                value: "12"
            }
        ],
        [t]
    )
    const [sensitivityRightMode, setSensitivityRightMode] = useState<SelectItem<string>>(
        selected == null
            ? SENSITIVITY_RIGHT_MODE_OPTIONS[0]
            : SENSITIVITY_RIGHT_MODE_OPTIONS.find(
                  sleepOption => sleepOption.value === selected?.lucent_config?.sensitivity_right_touch
              ) || null
    )

    const onSavePressed = useCallback(() => {
        const temperatureOffsetValue = Number(temperatureOffset)
        if (isTemperatureDevice) {
            if (isNaN(temperatureOffsetValue) || temperatureOffsetValue < -9.9 || temperatureOffsetValue > 9.9) {
                failure(t("temperature_offset_value_must_be_between_-9.9_9.9"))

                return
            }
        }

        const temperatureHysteresisValue = Number(temperatureHysteresis)
        if (isTemperatureDevice) {
            if (
                isNaN(temperatureHysteresisValue) ||
                temperatureHysteresisValue < 0 ||
                temperatureHysteresisValue > 25.5
            ) {
                failure(t("hysteresis_value_must_be_between_25.5_0"))

                return
            }
        }

        const firstLevelAlarmCountdownDurationValue = Number(firstLevelAlarmCountdownDuration)
        if (isNaN(firstLevelAlarmCountdownDurationValue) || firstLevelAlarmCountdownDurationValue < 0) {
            failure(t("alarm_level_1_countdown_duration_is_invalid"))

            return
        }

        const secondLevelAlarmCountdownDurationValue = Number(secondLevelAlarmCountdownDuration)
        if (isNaN(secondLevelAlarmCountdownDurationValue) || secondLevelAlarmCountdownDurationValue < 0) {
            failure(t("alarm_level_2_countdown_duration_is_invalid"))

            return
        }

        const thirdLevelAlarmCountdownDurationValue = Number(thirdLevelAlarmCountdownDuration)
        if (isNaN(thirdLevelAlarmCountdownDurationValue) || thirdLevelAlarmCountdownDurationValue < 0) {
            failure(t("alarm_level_3_countdown_duration_is_invalid"))

            return
        }

        const reactivationDurationAfterResetValue = Number(reactivationDurationAfterReset)
        if (isNaN(reactivationDurationAfterResetValue) || reactivationDurationAfterResetValue < 0) {
            failure(t("reactivation_after_alarm_reset_duration_is_invalid"))

            return
        }

        const sirenIntervalOnDurationValue = Number(sirenIntervalOnDuration)
        if (isNaN(sirenIntervalOnDurationValue) || sirenIntervalOnDurationValue < 0) {
            failure(t("siren_interval_on_duration_is_invalid"))

            return
        }

        const sirenIntervalOffDurationValue = Number(sirenIntervalOffDuration)
        if (isNaN(sirenIntervalOffDurationValue) || sirenIntervalOffDurationValue < 0) {
            failure(t("siren_interval_off_duration_is_invalid"))

            return
        }

        const sirenRepeatCountValue = Number(sirenRepeatCount)
        if (isNaN(sirenRepeatCountValue) || sirenRepeatCountValue < 0) {
            failure(t("siren_repeat_count_is_invalid"))

            return
        }

        const bridgeDeviceModel: BridgeDeviceModel = {
            ...selected,
            ...(isTemperatureDevice ? { temperature_offset: Math.round(temperatureOffsetValue * 10) } : {}),
            ...(isTemperatureDevice ? { temperature_hysteresis: Math.round(temperatureHysteresisValue * 10) } : {}),
            alarm_config: {
                first_level_alarm_countdown_duration: firstLevelAlarmCountdownDurationValue,
                second_level_alarm_countdown_duration: secondLevelAlarmCountdownDurationValue,
                third_level_alarm_countdown_duration: thirdLevelAlarmCountdownDurationValue,
                reactivation_duration_after_reset: reactivationDurationAfterResetValue,
                siren_interval_on_duration: sirenIntervalOnDurationValue,
                siren_interval_off_duration: sirenIntervalOffDurationValue,
                siren_interval_repeat_count: sirenRepeatCountValue
            },
            lucent_config: isGlassDevice
                ? null
                : {
                    is_sleep_mode_active: sleepMode?.value === 0 ? false : true,
                    sleep_time: sleepMode?.value || 0,
                    sensitivity_mode: sensitivityMode?.value || "0",
                    sensitivity_right_touch: sensitivityRightMode?.value || "0",
                    sensitivity_left_touch: sensitivityLeftMode?.value || "0"
                }
        }

        if (isICMDevice){
            bridgeDeviceModel.icm_config ={
                duty: duty!.value,
                frequency_whole: frequency!.value,
                frequency_fraction: 0
            }
        }

        rpc.updateBridgeDevice(bridgeDeviceModel)
            .then(() => {
                success(t("successfully_saved"))
                onBackPressed()
            })
            .catch(failure)
    }, [
        t,
        isTemperatureDevice,
        temperatureOffset,
        temperatureHysteresis,
        firstLevelAlarmCountdownDuration,
        secondLevelAlarmCountdownDuration,
        thirdLevelAlarmCountdownDuration,
        reactivationDurationAfterReset,
        sirenIntervalOnDuration,
        sirenIntervalOffDuration,
        sirenRepeatCount,
        selected,
        success,
        failure,
        rpc,
        onBackPressed,
        sleepMode,
        sensitivityMode,
        sensitivityLeftMode,
        sensitivityRightMode,
        isGlassDevice,
        isICMDevice,
        duty,
        frequency
    ])

    const onResetPressed = useCallback(() => {
        question(t("will_be_reset_continue"), () => {
            rpc.resetDevice(selected.gateway_id, selected.unique_device_id)
                .then(() => {
                    success(t("device_successfully_reset"))
                    setSelected({...selected, unique_device_id: ""})
                })
                .catch(failure)
        })
    }, [t, selected, question, rpc, success, failure])

    const matchAgain = useCallback(() => {
        if (matchInProgress) {
            return
        }
        if (selected.unique_device_id === "") {
            return failure("Unique id bulunamadı.")
        }
        setMatchInProgress(true)
        rpc.reMatchDevice(selected.gateway_id, selected.unique_device_id, selected.id)
            .catch(failure)
            .finally(() => setMatchInProgress(false))
    }, [matchInProgress, selected, failure, rpc])

    const buttons = [
        {
            text: t("detailed_rf_test"),
            className: "blue",
            onClick: () =>
                history.push(`/settings/device/device-settings/${params?.id}/detailed-rf-test/${selected?.gateway_id}`),
            hidden:
                selected?.type_id === BRIDGE_DEVICE_TYPE_ID_EIO ||
                selected?.type_id === BRIDGE_DEVICE_TYPE_ID_INO_PAN_3 ||
                selected?.type_id === BRIDGE_DEVICE_TYPE_ID_KNX
        },
        {
            text: matchInProgress ? t("matching") : t("again") + " " + t("match"),
            className: "blue",
            onClick: matchAgain
        },
        {
            text: t("reset"),
            className: "orange",
            onClick: onResetPressed,
            hidden: !selected?.unique_device_id
        },
        {
            text: t("save"),
            className: "green",
            onClick: onSavePressed
        },
        {
            text: t("cancel"),
            className: "red",
            url: BACK_URL
        }
    ]

    const onBlur = useCallback(() => {
        setActiveNumpadInput(null)
        setActiveNumpadExtendedInput(null)
    }, [setActiveNumpadInput, setActiveNumpadExtendedInput])

    useEffect(() => {
        if (params.id == null) {
            return
        }

        const device = bridgeDeviceList.find(device => device.id === params["id"])
        if (device == null) {
            return
        }

        setSelected(device)
        setTemperatureDevice(bridgeDeviceTypeSet[device.type_id]?.is_temperature_device || false)
        setTemperatureOffset((device.temperature_offset / 10).toFixed(1))
        setTemperatureHysteresis((device.temperature_hysteresis / 10).toFixed(1))
        setFirstLevelAlarmCountdownDuration(device.alarm_config.first_level_alarm_countdown_duration.toString())
        setSecondLevelAlarmCountdownDuration(device.alarm_config.second_level_alarm_countdown_duration.toString())
        setThirdLevelAlarmCountdownDuration(device.alarm_config.third_level_alarm_countdown_duration.toString())
        setReactivationDurationAfterReset(device.alarm_config.reactivation_duration_after_reset.toString())
        setSirenIntervalOnDuration(device.alarm_config.siren_interval_on_duration.toString())
        setSirenIntervalOffDuration(device.alarm_config.siren_interval_off_duration.toString())
        setSirenRepeatCount(device.alarm_config.siren_interval_repeat_count.toString())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (params.id == null) {
            return
        }

        const device = bridgeDeviceList.find(device => device.id === params["id"])
        if (device == null) {
            return
        }

        setSleepMode(SLEEP_MODE_OPTIONS.find(option => option.value === device?.lucent_config?.sleep_time) || SLEEP_MODE_OPTIONS[0])
        setSensitivityMode(
            SENSITIVITY_MODE_OPTIONS.find(option => option.value === device?.lucent_config?.sensitivity_mode) ||
            SENSITIVITY_MODE_OPTIONS[0]
        )
        setSensitivityRightMode(
            SENSITIVITY_RIGHT_MODE_OPTIONS.find(
                option => option.value === device?.lucent_config?.sensitivity_right_touch
            ) || SENSITIVITY_RIGHT_MODE_OPTIONS[0]
        )
        setSensitivityLeftMode(
            SENSITIVITY_LEFT_MODE_OPTIONS.find(
                option => option.value === device?.lucent_config?.sensitivity_left_touch
            ) || SENSITIVITY_LEFT_MODE_OPTIONS[0]
        )
        setFrequency(
            FREQUENCY_OPTIONS.find(
                option => option.value === device.icm_config?.frequency_whole
            ) || null
        )
        setDuty(
            DUTY_OPTIONS.find(
                option => option.value === device.icm_config?.duty
            ) || null
        )
        // TODO, we need to set set frequency and duty here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SLEEP_MODE_OPTIONS, SENSITIVITY_MODE_OPTIONS, SENSITIVITY_RIGHT_MODE_OPTIONS, SENSITIVITY_LEFT_MODE_OPTIONS])

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}
            >
                {renderIf(selected)(() => (
                    <div className="content-divider">
                        <div className="content-wrapper">
                            {renderIf(isGlassDevice && selected?.type_id !== "101")(() => (
                                <>
                                    <div className="line">
                                        <span>{t("alarm_countdown_durations")}</span>
                                    </div>
                                    <SettingInputComponent
                                        placeholder={t("1st_level")}
                                        label={t("1st_level")}
                                        value={firstLevelAlarmCountdownDuration}
                                        setValue={setFirstLevelAlarmCountdownDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue="0"
                                        onBlur={onBlur}
                                    />
                                    <SettingInputComponent
                                        placeholder={t("2nd_level")}
                                        label={t("2nd_level")}
                                        value={secondLevelAlarmCountdownDuration}
                                        setValue={setSecondLevelAlarmCountdownDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue="0"
                                        onBlur={onBlur}
                                    />
                                    <SettingInputComponent
                                        placeholder={t("3rd_level")}
                                        label={t("3rd_level")}
                                        value={thirdLevelAlarmCountdownDuration}
                                        setValue={setThirdLevelAlarmCountdownDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue="0"
                                        onBlur={onBlur}
                                    />
                                    <div className="line">
                                        <span>{t("alar_reactivation_duration_after_reset")}</span>
                                    </div>
                                    <SettingInputComponent
                                        placeholder={t("reactivation_duration")}
                                        label={t("reactivation_duration_2")}
                                        value={reactivationDurationAfterReset}
                                        setValue={setReactivationDurationAfterReset}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue="0"
                                        onBlur={onBlur}
                                    />
                                </>
                            ))}
                            {renderIf(isICMDevice && frequency && duty)(() => (
                                <>
                                    <div className="line">
                                        <span>{t("ir_settings")}</span>
                                    </div>
                                    <SettingSelectComponent
                                        label={t("Frequency")}
                                        options={FREQUENCY_OPTIONS}
                                        onChange={setFrequency}
                                        value={frequency}
                                    />
                                    <SettingSelectComponent
                                        label={t("Duty")}
                                        options={DUTY_OPTIONS}
                                        onChange={setDuty}
                                        value={duty}
                                    />
                                </>
                            ))}
                        </div>

                        <div className="content-wrapper">
                            <>
                                {renderIf(selected?.type_id !== "101")(() => (
                                    <>
                                        {renderIf(isGlassDevice)(() => (
                                            <>
                                                <div className="line">
                                                    <span>{t("siren_interval")}</span>
                                                </div>
                                                <SettingInputComponent
                                                    placeholder={t("interval_on_duration")}
                                                    label={t("interval_on_duration_2")}
                                                    value={sirenIntervalOnDuration}
                                                    setValue={setSirenIntervalOnDuration}
                                                    setActiveInput={setActiveNumpadInput}
                                                    tempValue="0"
                                                    onBlur={onBlur}
                                                />
                                                <SettingInputComponent
                                                    placeholder={t("interval_off_duration")}
                                                    label={t("interval_off_duration_2")}
                                                    value={sirenIntervalOffDuration}
                                                    setValue={setSirenIntervalOffDuration}
                                                    setActiveInput={setActiveNumpadInput}
                                                    tempValue="0"
                                                    onBlur={onBlur}
                                                />
                                                <SettingInputComponent
                                                    placeholder={t("interval_repeat_count")}
                                                    label={t("interval_repeat_count_2")}
                                                    value={sirenRepeatCount}
                                                    setValue={setSirenRepeatCount}
                                                    setActiveInput={setActiveNumpadInput}
                                                    tempValue="0"
                                                    onBlur={onBlur}
                                                />
                                            </>
                                        ))}
                                        <div className="line">
                                            <span>{t("temperature")}</span>
                                        </div>
                                        <SettingInputComponent
                                            placeholder={t("temperature_offset_value")}
                                            label={t("temperature_offset")}
                                            value={temperatureOffset}
                                            setValue={setTemperatureOffset}
                                            setActiveInput={setActiveNumpadExtendedInput}
                                            tempValue="0"
                                            onBlur={onBlur}
                                        />
                                        <SettingInputComponent
                                            placeholder={t("temperature_hysteresis_value")}
                                            label={t("temperature_hysteresis")}
                                            value={temperatureHysteresis}
                                            setValue={setTemperatureHysteresis}
                                            setActiveInput={setActiveNumpadExtendedInput}
                                            tempValue="0"
                                            onBlur={onBlur}
                                        />
                                        {renderIf(!isGlassDevice)(() => (
                                            <>
                                                <div className="line">
                                                    <span>{t("button").toUpperCase()}</span>
                                                </div>
                                                <SettingSelectComponent
                                                    label={t("Uyku Modu")}
                                                    options={SLEEP_MODE_OPTIONS}
                                                    onChange={setSleepMode}
                                                    value={sleepMode}
                                                />
                                                {renderIf(
                                                    selected?.type_id !== BRIDGE_DEVICE_TYPE_ID_STP &&
                                                        selected?.type_id !== BRIDGE_DEVICE_TYPE_ID_STS_PLUS
                                                )(
                                                    () => (
                                                        <SettingSelectComponent
                                                            label={t("Hassaslık")}
                                                            options={SENSITIVITY_MODE_OPTIONS}
                                                            onChange={setSensitivityMode}
                                                            value={sensitivityMode}
                                                        />
                                                    ),
                                                    () => (
                                                        <>
                                                            <SettingSelectComponent
                                                                label={t("Sol Buton Hassasiyet")}
                                                                options={SENSITIVITY_LEFT_MODE_OPTIONS}
                                                                onChange={setSensitivityLeftMode}
                                                                value={sensitivityLeftMode}
                                                            />
                                                            <SettingSelectComponent
                                                                label={t("Sağ Buton Hassasiyet")}
                                                                options={SENSITIVITY_RIGHT_MODE_OPTIONS}
                                                                onChange={setSensitivityRightMode}
                                                                value={sensitivityRightMode}
                                                            />
                                                        </>
                                                    )
                                                )}
                                            </>
                                        ))}
                                    </>
                                ))}
                            </>
                        </div>
                    </div>
                ))}
            </SettingsPageContainer>
            {renderNumpadKeyboard()}
            {renderNumpadExtendedKeyboard()}
        </PasswordContainer>
    )
})
